<template>
  <div class="container mt-5">
    <h1 class="title is-3">Celery Flower Dashboard</h1>

    <!-- Tabs -->
    <div class="tabs is-toggle is-toggle-rounded">
      <ul>
        <li :class="{ 'is-active': activeTab === 'overview' }"><a @click="activeTab = 'overview'">Overview</a></li>
        <li :class="{ 'is-active': activeTab === 'tasks' }"><a @click="activeTab = 'tasks'">Tasks</a></li>
        <li :class="{ 'is-active': activeTab === 'manual' }"><a @click="activeTab = 'manual'">Manual Trigger</a></li>
      </ul>
    </div>

    <!-- Overview Tab -->
    <div v-if="activeTab === 'overview'">
      <div class="field is-grouped mb-4">
        <p class="control"><button class="button is-primary is-small" @click="refreshOverview">Refresh All</button></p>
      </div>
      <!-- Workers -->
      <section class="box">
        <h2 class="subtitle is-4">Workers</h2>
        <table class="table is-fullwidth is-bordered is-striped is-small">
          <thead>
            <tr>
              <th>Worker</th>
              <th>Concurrency</th>
              <th>Queues</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(w, name) in workers" :key="name">
              <td>{{ name }}</td>
              <td>{{ w.stats.pool['max-concurrency'] }}</td>
              <td>
                <span v-for="q in w.active_queues" :key="q.name" class="tag is-light mr-1">{{ q.name }}</span>
              </td>
              <td>
                <button class="button is-danger is-small mr-1" @click="shutdownWorker(name)">Shutdown</button>
                <button class="button is-warning is-small" @click="restartPool(name)">Restart Pool</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Queues -->
      <section class="box mt-5">
        <h2 class="subtitle is-4">Queues</h2>
        <table class="table is-fullwidth is-bordered is-striped is-small">
          <thead>
            <tr>
              <th>Name</th>
              <th>Messages</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="q in queues" :key="q.name">
              <td>{{ q.name }}</td>
              <td>{{ q.messages }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <!-- Tasks Tab -->
    <div v-if="activeTab === 'tasks'">
      <div class="field is-grouped mb-4">
        <p class="control is-expanded">
          <input v-model="searchQuery" class="input is-small" placeholder="Search by ID, name, or status..." />
        </p>
      </div>
      <section class="box">
        <h2 class="subtitle is-4">Recent Tasks</h2>
        <table class="table is-fullwidth is-bordered is-striped is-small">
          <thead>
            <tr>
              <th @click="setSort('received')">Received <span v-if="sortBy==='received'">{{ sortDesc ? '▼' : '▲' }}</span></th>
              <th @click="setSort('started')">Started <span v-if="sortBy==='started'">{{ sortDesc ? '▼' : '▲' }}</span></th>
              <th>ID</th>
              <th>Name</th>
              <th @click="setSort('state')">Status <span v-if="sortBy==='state'">{{ sortDesc ? '▼' : '▲' }}</span></th>
              <th>Worker</th>
              <th @click="setSort('runtime')">Run Time (s) <span v-if="sortBy==='runtime'">{{ sortDesc ? '▼' : '▲' }}</span></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in sortedTasks" :key="task.uuid">
              <td>{{ formatTime(task.received) }}</td>
              <td>{{ formatTime(task.started) }}</td>
              <td><a @click.prevent="viewTaskInfo(task.uuid)">{{ task.uuid }}</a></td>
              <td>{{ task.name }}</td>
              <td><span class="tag" :class="statusClass(task.state)">{{ task.state }}</span></td>
              <td>{{ task.worker }}</td>
              <td>{{ task.runtime ? task.runtime.toFixed(2) : '-' }}</td>
              <td>
                <button class="button is-success is-small" @click="getTaskResult(task.uuid)">Result</button>
                <button class="button is-info is-small ml-1" @click="immediateRetry(task)">Retry</button>
                <button class="button is-dark is-small ml-1" @click="revokeTask(task.uuid)">Revoke</button>
                <button class="button is-warning is-small ml-1" @click="openTimeoutModal(task)">Timeout</button>
                <button class="button is-primary is-small ml-1" @click="openRateLimitModal(task)">Rate Limit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <!-- Manual Trigger Tab -->
    <div v-if="activeTab === 'manual'">
      <section class="box">
        <h2 class="subtitle is-4">Manual Task Execution</h2>
        <div class="field">
          <label class="label">Task Name</label>
          <div class="control">
            <input v-model="manual.name" class="input is-small" placeholder="tasks.add" />
          </div>
        </div>
        <div class="field">
          <label class="label">Method</label>
          <div class="control">
            <div class="select is-small">
              <select v-model="manual.method">
                <option value="apply">apply (wait)</option>
                <option value="async-apply">async-apply</option>
                <option value="send-task">send-task</option>
              </select>
            </div>
          </div>
        </div>
        <div class="field">
          <label class="label">Args (JSON Array)</label>
          <div class="control">
            <textarea v-model="manual.args" class="textarea is-small" placeholder='[1, 2]'></textarea>
          </div>
        </div>
        <div class="field">
          <label class="label">Kwargs (JSON Object)</label>
          <div class="control">
            <textarea v-model="manual.kwargs" class="textarea is-small" placeholder='{"foo": "bar"}'></textarea>
          </div>
        </div>
        <div class="field">
          <div class="control">
            <button class="button is-link is-small" @click="manualExecute">Execute</button>
            <button class="button is-light is-small ml-2" @click="clearManual">Clear</button>
          </div>
        </div>
        <div v-if="manual.result" class="notification is-info is-small">
          <strong>Result:</strong>
          <pre>{{ manual.result }}</pre>
        </div>
      </section>
    </div>

    <!-- Info Modal -->
    <div class="modal" :class="{ 'is-active': showModal }">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-card" style="width: 85%;">
        <header class="modal-card-head">
          <p class="modal-card-title">Task Info: {{ infoId }}</p>
          <button class="delete" @click="closeModal"></button>
        </header>
        <section class="modal-card-body">
          <pre>{{ infoData }}</pre>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-small" @click="closeModal">Close</button>
          <button class="button is-success is-small" @click="refreshInfo">Refresh</button>
        </footer>
      </div>
    </div>

    <!-- Timeout Modal -->
    <div class="modal" :class="{ 'is-active': showTimeoutModal }">
      <div class="modal-background" @click="closeTimeoutModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Set Timeout for {{ timeoutParams.name }}</p>
          <button class="delete" @click="closeTimeoutModal"></button>
        </header>
        <section class="modal-card-body is-small">
          <div class="field">
            <label class="label">Soft Time Limit (s)</label>
            <div class="control"><input v-model.number="timeoutParams.soft" type="number" class="input is-small" /></div>
          </div>
          <div class="field">
            <label class="label">Hard Time Limit (s)
</label>
            <div class="control"><input v-model.number="timeoutParams.hard" type="number" class="input is-small" /></div>
          </div>
          <div class="field">
            <label class="label">Worker Name</label>
            <div class="control"><input v-model="timeoutParams.workername" class="input is-small" /></div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success is-small" @click="submitTimeout">Save</button>
          <button class="button is-light is-small" @click="closeTimeoutModal">Cancel</button>
        </footer>
      </div>
    </div>

    <!-- Rate Limit Modal -->
    <div class="modal" :class="{ 'is-active': showRateModal }">
      <div class="modal-background" @click="closeRateLimitModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Set Rate Limit for {{ rateLimitParams.name }}</p>
          <button class="delete" @click="closeRateLimitModal"></button>
        </header>
        <section class="modal-card-body is-small">
          <div class="field">
            <label class="label">Rate Limit (tasks/min)</label>
            <div class="control"><input v-model.number="rateLimitParams.ratelimit" type="number" class="input is-small" /></div>
          </div>
          <div class="field">
            <label class="label">Worker Name</label>
            <div class="control"><input v-model="rateLimitParams.workername" class="input is-small" /></div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-primary is-small" @click="submitRateLimit">Save</button>
          <button class="button is-light is-small" @click="closeRateLimitModal">Cancel</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const API = 'https://aml-dev.despaq.com/flower/api';
export default {
  data() {
    return {
      activeTab: 'overview',
      searchQuery: '',
      workers: {}, queues: [], tasks: [],
      showModal: false, infoId: null, infoData: {},
      showTimeoutModal: false, timeoutParams: { name: '', soft: 30, hard: 100, workername: '' },
      showRateModal: false, rateLimitParams: { name: '', ratelimit: 200, workername: '' },
      sortBy: 'received', sortDesc: true,
      manual: { name: '', method: 'async-apply', args: '[]', kwargs: '{}', result: null }
    };
  },
  computed: {
    filteredTasks() {
      const q = this.searchQuery.toLowerCase();
      return this.tasks.filter(t =>
        t.uuid.toLowerCase().includes(q) ||
        (t.name || '').toLowerCase().includes(q) ||
        (t.state || '').toLowerCase().includes(q)
      );
    },
    sortedTasks() {
      return [...this.filteredTasks].sort((a, b) => {
        let va = a[this.sortBy] || ''; let vb = b[this.sortBy] || '';
        if (this.sortBy === 'state') {
          return this.sortDesc ? vb.localeCompare(va) : va.localeCompare(vb);
        }
        return this.sortDesc ? vb - va : va - vb;
      });
    }
  },
  methods: {
    setSort(key) { if (this.sortBy === key) this.sortDesc = !this.sortDesc; else { this.sortBy = key; this.sortDesc = false; } },
    formatTime(ts) { return ts ? new Date(ts * 1000).toLocaleTimeString() : '-'; },
    async refreshOverview() { await Promise.all([this.loadWorkers(), this.loadQueues(), this.loadTasks()]); },
    async loadWorkers() { const { data } = await axios.get(`${API}/workers?refresh=true`); this.workers = data; },
    async loadQueues() { const { data } = await axios.get(`${API}/queues/length`); this.queues = data.active_queues; },
    async loadTasks() { const { data } = await axios.get(`${API}/tasks?limit=100`); this.tasks = Object.values(data); },
    async viewTaskInfo(id) { this.infoId = id; await this.refreshInfo(); this.showModal = true; },
    async refreshInfo() { const { data } = await axios.get(`${API}/task/info/${this.infoId}`); this.infoData = data; },
    closeModal() { this.showModal = false; this.infoData = {}; this.infoId = null; },
    statusClass(s) { return { SUCCESS: 'is-success', STARTED: 'is-warning', FAILURE: 'is-danger', REVOKED: 'is-dark', PENDING: 'is-light' }[s] || 'is-light'; },
    async abortTask(id) { await axios.post(`${API}/task/abort/${id}`); this.loadTasks(); },
    async revokeTask(id) { await axios.post(`${API}/task/revoke/${id}?terminate=true&signal=SIGTERM`); this.loadTasks(); },
    async getTaskResult(id) { const { data } = await axios.get(`${API}/task/result/${id}`); alert(JSON.stringify(data, null, 2)); },
    async immediateRetry(task) {
      try {
        const rawArgs = task.args || '[]';
        const rawKwargs = task.kwargs || '{}';
        const args = JSON.parse(rawArgs.replace(/'/g, '"')); const kwargs = JSON.parse(rawKwargs.replace(/'/g, '"'));
        const { data } = await axios.post(`${API}/task/async-apply/${task.name}`, { args, kwargs });
        alert(`Task retriggered: ${data['task-id']}`);
        this.loadTasks();
      } catch (e) { console.error(e); alert('Failed to retry task.'); }
    },
    openTimeoutModal(task) { this.timeoutParams = { name: task.name, soft: 30, hard: 100, workername: task.worker }; this.showTimeoutModal = true; },
    closeTimeoutModal() { this.showTimeoutModal = false; },
    submitTimeout() {
      const { name, soft, hard, workername } = this.timeoutParams;
      axios.post(`${API}/task/timeout/${encodeURIComponent(name)}`, new URLSearchParams({ soft, hard, workername }))
        .then(() => { this.showTimeoutModal = false; this.loadTasks(); })
        .catch(console.error);
    },
    openRateLimitModal(task) { this.rateLimitParams = { name: task.name, ratelimit: 200, workername: task.worker }; this.showRateModal = true; },
    closeRateLimitModal() { this.showRateModal = false; },
    submitRateLimit() {
      const { name, ratelimit, workername } = this.rateLimitParams;
      axios.post(`${API}/task/rate-limit/${encodeURIComponent(name)}`, new URLSearchParams({ ratelimit, workername }))
        .then(() => { this.showRateModal = false; this.loadTasks(); })
        .catch(console.error);
    },
    retriggerTask(task) { this.manual.name = task.name; this.manual.method = 'async-apply'; this.manual.args = task.args; this.manual.kwargs = task.kwargs; this.manual.result = null; this.activeTab = 'manual'; },
    clearManual() { this.manual = { name: '', method: 'async-apply', args: '[]', kwargs: '{}', result: null }; },
    async manualExecute() {
      try {
        const args = JSON.parse(this.manual.args); const kwargs = JSON.parse(this.manual.kwargs);
        const { data } = await axios.post(`${API}/task/${this.manual.method}/${this.manual.name}`, { args, kwargs });
        this.manual.result = data;
      } catch(e) { console.error(e); alert('Execution failed'); }
    }
  },
  mounted() { this.refreshOverview(); }
};
</script>

<style scoped>
.container { max-width: 1200px; margin: auto; }
pre { white-space: pre-wrap; word-break: break-word; background: #f5f5f5; padding: 1rem; border-radius: 4px; }
.ml-1 { margin-left: 0.5rem; }
.ml-2 { margin-left: 1rem; }
</style>
